// src/admin/screens/borrowers/modals/ResetPhoneModal.tsx
// Replaces the phone on the profile. The borrower must re-verify via
// OTP on next sign-in — the borrower app reads profiles.phone as the
// source of truth. We write the old + new phone to the audit log so
// stolen-device investigations have an immutable trail.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import { isValidZambiaPhone } from '@admin/utils/phone';
import type { BorrowerDetail } from '@admin/hooks/useBorrower';

const schema = z.object({
  new_phone: z.string().refine(isValidZambiaPhone, 'Invalid Zambian mobile number'),
  reason: z.string().min(3, 'Reason required'),
});
type Values = z.infer<typeof schema>;

interface Props {
  borrower: BorrowerDetail;
  onClose: (refresh: boolean) => void;
}

export default function ResetPhoneModal({ borrower, onClose }: Props) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const submit = async (v: Values) => {
    setBusy(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ phone: v.new_phone })
        .eq('id', borrower.id);
      if (error) throw error;
      await recordAudit({
        action: 'borrower.reset_phone',
        target_type: 'borrower',
        target_id: borrower.id,
        payload: {
          old_phone: borrower.phone,
          new_phone: v.new_phone,
          reason: v.reason,
        },
      });
      toast({
        kind: 'success',
        msg: 'Phone reset. Borrower must re-verify on next login.',
      });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={() => onClose(false)} title="Reset borrower phone">
      <form
        onSubmit={handleSubmit(submit)}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <p className="small">
          This replaces the phone on record. The next OTP will go to the new number. Use cautiously
          — stolen-device scenarios require an out-of-band verification.
        </p>
        <FormField
          label="New phone (+260…)"
          htmlFor="new-phone"
          error={errors.new_phone?.message}
          required
        >
          <input id="new-phone" placeholder="+260977123456" {...register('new_phone')} />
        </FormField>
        <FormField label="Reason" htmlFor="reset-reason" error={errors.reason?.message} required>
          <textarea id="reset-reason" rows={3} {...register('reason')} />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={() => onClose(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Reset phone
          </Button>
        </div>
      </form>
    </Modal>
  );
}
